import "./styles.css";

import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// The type of messages we'll be receiving from the server
import type { OutgoingMessage } from "../shared";
import type { LegacyRef } from "react";
import { PlanetaryRegistry } from "./PlanetaryRegistry";

type MiniPlanet = {
	name: string;
	color: string;
	orbitRadius: number;
	size: number;
	orbitDuration: number;
	startAngle: number;
};

const MINI_PLANETS: MiniPlanet[] = [
	{ name: "Mercury", color: "linear-gradient(135deg, #a8b2ff, #6471ff)", orbitRadius: 140, size: 12, orbitDuration: 22, startAngle: 12 },
	{ name: "Venus", color: "linear-gradient(135deg, #f9a8d4, #f472b6)", orbitRadius: 170, size: 14, orbitDuration: 26, startAngle: 84 },
	{ name: "Mars", color: "linear-gradient(135deg, #fb7185, #f97316)", orbitRadius: 200, size: 14, orbitDuration: 28, startAngle: 140 },
	{ name: "Jupiter", color: "linear-gradient(135deg, #fbbf24, #f59e0b)", orbitRadius: 230, size: 18, orbitDuration: 34, startAngle: 205 },
	{ name: "Saturn", color: "linear-gradient(135deg, #f5e0b3, #e5c07b)", orbitRadius: 255, size: 16, orbitDuration: 38, startAngle: 260 },
	{ name: "Sun", color: "radial-gradient(circle at 30% 30%, #fde68a, #fbbf24, #f59e0b)", orbitRadius: 125, size: 18, orbitDuration: 30, startAngle: 315 },
	{ name: "Moon", color: "radial-gradient(circle at 30% 30%, #e5e7eb, #cbd5e1)", orbitRadius: 185, size: 13, orbitDuration: 18, startAngle: 45 },
];

function FloatingPlanets() {
	return (
		<div className="floating-planets" aria-hidden="true">
			{MINI_PLANETS.map((planet) => (
				<div
					key={planet.name}
					className="planet-orb"
					style={{
						background: planet.color,
						width: `${planet.size}px`,
						height: `${planet.size}px`,
						["--orbit-radius" as "--orbit-radius"]: `${planet.orbitRadius}px`,
						["--orbit-duration" as "--orbit-duration"]: `${planet.orbitDuration}s`,
						["--start-angle" as "--start-angle"]: `${planet.startAngle}deg`,
					}}
				>
					<span className="planet-label">{planet.name}</span>
				</div>
			))}
		</div>
	);
}

function App() {
	// A reference to the canvas element where we'll render the globe
	const canvasRef = useRef<HTMLCanvasElement>();
	// The number of markers we're currently displaying
	const [counter, setCounter] = useState(0);
	// A map of marker IDs to their positions
	// Note that we use a ref because the globe's `onRender` callback
	// is called on every animation frame, and we don't want to re-render
	// the component on every frame.
	const positions = useRef<
		Map<
			string,
			{
				location: [number, number];
				size: number;
			}
		>
	>(new Map());
	// Connect to the PartyServer server
	const socket = usePartySocket({
		room: "default",
		party: "globe",
		onMessage(evt) {
			const message = JSON.parse(evt.data as string) as OutgoingMessage;
			if (message.type === "add-marker") {
				// Add the marker to our map
				positions.current.set(message.position.id, {
					location: [message.position.lat, message.position.lng],
					size: message.position.id === socket.id ? 0.1 : 0.05,
				});
				// Update the counter
				setCounter((c) => c + 1);
			} else {
				// Remove the marker from our map
				positions.current.delete(message.id);
				// Update the counter
				setCounter((c) => c - 1);
			}
		},
	});

	useEffect(() => {
		// The angle of rotation of the globe
		// We'll update this on every frame to make the globe spin
		let phi = 0;

		const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
			devicePixelRatio: 2,
			width: 400 * 2,
			height: 400 * 2,
			phi: 0,
			theta: 0,
			dark: 1,
			diffuse: 0.8,
			mapSamples: 16000,
			mapBrightness: 6,
			baseColor: [0.3, 0.3, 0.3],
			markerColor: [0.8, 0.1, 0.1],
			glowColor: [0.2, 0.2, 0.2],
			markers: [],
			opacity: 0.7,
			onRender: (state) => {
				// Called on every animation frame.
				// `state` will be an empty object, return updated params.

				// Get the current positions from our map
				state.markers = [...positions.current.values()];

				// Rotate the globe
				state.phi = phi;
				phi += 0.01;
			},
		});

		return () => {
			globe.destroy();
		};
	}, []);

	return (
		<div className="App">
			<div className="hero-grid">
				<div className="hero-copy">
					<p className="eyebrow">Live celestial energy map</p>
					<h1>Atirica</h1>
					<p className="lede">
						Track planetary dignities, aspects, and elemental shifts in real time — grounded to your location.
					</p>
					<div className="connection-chip">
						<span className="pulse" aria-hidden="true" />
						{counter === 0 ? "Waiting for fellow travelers" : `${counter} ${counter === 1 ? "person" : "people"} connected`}
					</div>
				</div>

				<div className="globe-card">
					<div className="globe-aura" />
					<FloatingPlanets />
					<canvas
						ref={canvasRef as LegacyRef<HTMLCanvasElement>}
						className="globe-canvas"
						style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: 1 }}
					/>
				</div>
			</div>

			{/* Planetary Energy Registry */}
			<PlanetaryRegistry />

			{/* Footer */}
			<footer className="sanctum-footer">
				<div className="footer-content">
					<p className="footer-link">
						Make a custom website like this for <span className="price-highlight">$7</span> a month at{" "}
						<a href="https://sanctumnetwork.com" target="_blank" rel="noopener noreferrer" className="footer-link-text">
							sanctumnetwork.com
						</a>
					</p>
					<p className="footer-copyright">
						© 2025 Sanctum Network
					</p>
				</div>
			</footer>
		</div>
	);
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);





			{/* Let's give some credit later after production
			<p>
				Powered by <a href="https://cobe.vercel.app/">🌏 Cobe</a>,{" "}
				<a href="https://www.npmjs.com/package/phenomenon">Phenomenon</a> and{" "}
				<a href="https://npmjs.com/package/partyserver/">🎈 PartyServer</a>
			</p>
			*/}